import * as React from 'react';
import { Plus } from 'lucide-react';

import { PageHeader } from '@/components/ui/page-header';
import { TableCard } from '@/components/ui/table-card';
import { Button } from '@/components/ui/button';
import { useBlockRulesList, useDeleteBlockRules, BlockRule } from '@/lib/api/blockRules';
import { useTablePage } from '@/hooks/use-table-page';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { BlockRulesTable } from './components/block-rules-table';
import { blockRulesSortMapping } from './utils/table-mappings';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(BlockRulesPage, PERMISSIONS.PROCESSORS_VIEW);

function BlockRulesPage() {
    const { orgRoutes, navigate } = useOrgNavigate();

    const { tableState, setTableState, queryInput } = useTablePage({
        initialSorting: [{ id: 'createdAt', desc: true }],
        sortMapping: blockRulesSortMapping,
    });

    const {
        data: listResponse,
        isLoading: loading,
        isFetching: fetching,
        refetch,
    } = useBlockRulesList(queryInput);

    const { mutateAsync: deleteBlockRules, isPending: isDeleting } = useDeleteBlockRules();

    const blockRules = listResponse?.blockRules ?? [];
    const total = listResponse?.total ?? 0;

    const handleCreate = React.useCallback(() => {
        navigate(orgRoutes.BLOCK_RULE_CREATE());
    }, [navigate, orgRoutes]);

    const handleRowClick = React.useCallback((blockRule: BlockRule) => {
        navigate(orgRoutes.BLOCK_RULE_DETAILS(blockRule.id));
    }, [navigate, orgRoutes]);

    const handleDelete = React.useCallback(
        async (ids: string[]) => {
            await deleteBlockRules({ ids });
        },
        [deleteBlockRules],
    );

    return (
        <div className="flex flex-col space-y-4 sm:space-y-6">
            <PageHeader
                title="Block Rules"
                description="Manage fraud block rules to prevent suspicious payments."
            >
                <Button onClick={handleCreate}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add block rule
                </Button>
            </PageHeader>

            <TableCard>
                <BlockRulesTable
                    blockRules={blockRules}
                    total={total}
                    loading={loading || fetching}
                    onTableStateChange={setTableState}
                    onRefetch={refetch}
                    onRowClick={handleRowClick}
                    onDelete={handleDelete}
                    isDeleting={isDeleting}
                />
            </TableCard>
        </div>
    );
}
